import { VolatileStorage } from 'src/storage/volatile-storage';

const BUCKET_KEY = 'update_bucket';
const volatileStorage = new VolatileStorage();

/**
 * Get the device bucket for staged rollout eligibility
 * Each device is assigned a stable bucket number (0-99)
 * This ensures consistent rollout behavior across app restarts
 *
 * @returns Promise<number> Device bucket (0-99)
 */
export async function getDeviceBucket(): Promise<number> {
  const bucket = volatileStorage.getString(BUCKET_KEY);

  if (bucket !== null) {
    return Number(bucket);
  }

  // Generate stable bucket using device ID
  const deviceId = await getDeviceId();
  const stableBucket = generateStableBucket(deviceId);

  volatileStorage.setString(BUCKET_KEY, stableBucket.toString());

  return stableBucket;
}

/**
 * Get device identifier from Capacitor Device plugin
 *
 * @returns Promise<string> Device identifier
 */
async function getDeviceId(): Promise<string> {
  try {
    const { Device } = await import('@capacitor/device');
    const info = await Device.getInfo();
    // DeviceInfo may not have identifier property, use id as fallback
    return (info as any).identifier || (info as any).id || generateFallbackId();
  } catch (error) {
    console.warn('Failed to get device ID, using fallback:', error);
    return generateFallbackId();
  }
}

/**
 * Generate a stable bucket number (0-99) from device ID
 * Uses a simple hash algorithm to ensure consistency
 *
 * @param deviceId Device identifier string
 * @returns number Bucket number (0-99)
 */
function generateStableBucket(deviceId: string): number {
  let hash = 0;
  for (let i = 0; i < deviceId.length; i++) {
    const char = deviceId.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash) % 100;
}

/**
 * Generate a fallback UUID if device ID is unavailable
 *
 * @returns string UUID string
 */
function generateFallbackId(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
